import React, { useEffect, useState, useRef } from "react";
import { Modal, Button, Row, Col, Form, Image, Spinner, InputGroup } from "react-bootstrap";
import { AiOutlineCheck, AiOutlineClose, AiOutlineFullscreen, AiOutlineDelete, AiOutlineEdit } from "react-icons/ai";
import axios from "axios";
import DriverSelector from "./driverSelector";

const ZOOM_LEVELS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2, 2.5, 3];

/** Зображення фактури з масштабуванням та відкриттям у повному розмірі */
const InvoiceImageZoom = ({ imageUrl, imageLoading, imageError, label }) => {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [imageSize, setImageSize] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!imageUrl) {
      setImageSize(null);
      setZoomLevel(1);
    }
  }, [imageUrl]);

  useEffect(() => {
    if (!imageSize || !containerRef.current) return;
    const el = containerRef.current;
    const measure = () => {
      const W = el.clientWidth;
      const H = el.clientHeight;
      if (W <= 0 || H <= 0) return;
      const fitScale = Math.min(W / imageSize.w, H / imageSize.h);
      const capped = Math.min(Math.max(fitScale, ZOOM_LEVELS[0]), 1);
      setZoomLevel(capped);
    };
    const t = setTimeout(measure, 0);
    return () => clearTimeout(t);
  }, [imageSize]);

  const handleImageLoad = (e) => {
    const { naturalWidth: w, naturalHeight: h } = e.target;
    setImageSize({ w, h });
  };

  const zoomIn = () => {
    setZoomLevel((z) => {
      const next = ZOOM_LEVELS.find((level) => level > z);
      return next != null ? next : ZOOM_LEVELS[ZOOM_LEVELS.length - 1];
    });
  };

  const zoomOut = () => {
    setZoomLevel((z) => {
      const prev = [...ZOOM_LEVELS].reverse().find((level) => level < z);
      return prev != null ? prev : ZOOM_LEVELS[0];
    });
  };

  const openFullSize = () => {
    if (imageUrl) window.open(imageUrl, "_blank", "noopener,noreferrer");
  };

  if (imageLoading) {
    return (
      <div className="border rounded p-2 d-flex align-items-center justify-content-center" style={{ minHeight: 260 }}>
        <Spinner animation="border" />
      </div>
    );
  }
  if (imageError || !imageUrl) {
    return (
      <div className="border rounded p-2 d-flex align-items-center justify-content-center text-muted small" style={{ minHeight: 260 }}>
        {imageError || "Зображення відсутнє"}
      </div>
    );
  }

  return (
    <div>
      {label && <div className="small text-muted mb-1">{label}</div>}
      <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
        <div className="d-flex align-items-center">
          <Button size="sm" variant="outline-secondary" onClick={zoomOut} aria-label="Зменшити">−</Button>
          <span className="px-2 small" style={{ minWidth: 48 }}>{Math.round(zoomLevel * 100)}%</span>
          <Button size="sm" variant="outline-secondary" onClick={zoomIn} aria-label="Збільшити">+</Button>
        </div>
        <Button size="sm" variant="outline-primary" onClick={openFullSize}>
          <AiOutlineFullscreen className="me-1" />
          Відкрити в повному розмірі
        </Button>
      </div>
      <div
        ref={containerRef}
        className="border rounded overflow-auto bg-dark bg-opacity-10"
        style={{ minHeight: 260, maxHeight: 380 }}
      >
        {imageSize ? (
          <div
            style={{
              width: imageSize.w * zoomLevel,
              height: imageSize.h * zoomLevel,
              minWidth: "100%",
              minHeight: 260,
            }}
          >
            <img
              src={imageUrl}
              alt="Фактура"
              onLoad={handleImageLoad}
              style={{
                width: imageSize.w,
                height: imageSize.h,
                transform: `scale(${zoomLevel})`,
                transformOrigin: "0 0",
                display: "block",
              }}
            />
          </div>
        ) : (
          <div className="d-flex align-items-center justify-content-center h-100" style={{ minHeight: 260 }}>
            <img
              src={imageUrl}
              alt="Фактура"
              onLoad={handleImageLoad}
              style={{ maxHeight: 280, objectFit: "contain" }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const statusLabel = (s) => {
  const map = {
    uploaded: "Завантажена",
    approved: "Затверджена",
    paid: "Виплачена",
    rejected: "Відхилена",
  };
  return map[s] || s;
};

const statusVariant = (s) => {
  const map = { uploaded: "secondary", approved: "primary", paid: "success", rejected: "danger" };
  return map[s] || "light";
};

const sourceLabel = (src) => {
  if (src === "ksef") return "KSeF";
  if (src === "admin") return "Адмін";
  return "Водій";
};

const prettyPrintKsefData = (raw) => {
  if (raw == null || raw === "") return "";
  if (typeof raw === "object") {
    try {
      return JSON.stringify(raw, null, 2);
    } catch {
      return String(raw);
    }
  }
  const text = String(raw);
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
};

/**
 * Модальне вікно для перегляду фактури водія.
 * За замовчуванням — тільки перегляд; по кліку на олівець — режим редагування (Брутто, % від VAT, Ціна за фактуру, Статус).
 */
export const DriverInvoiceViewModal = ({ show, onHide, invoice, onSave, onDelete }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editVat, setEditVat] = useState("");
  const [editGross, setEditGross] = useState("");
  const [editRefundPercent, setEditRefundPercent] = useState("");
  const [editInvoiceFee, setEditInvoiceFee] = useState("");
  const [editStatus, setEditStatus] = useState("uploaded");
  const [editRejectionReason, setEditRejectionReason] = useState("");
  const [editError, setEditError] = useState("");
  const [saving, setSaving] = useState(false);
  const [showKsefRaw, setShowKsefRaw] = useState(false);

  useEffect(() => {
    if (!show || !invoice?.id) {
      setImageUrl(null);
      setImageError("");
      return;
    }
    let revoked = null;
    const load = async () => {
      setImageLoading(true);
      setImageError("");
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`/api/driver-invoices/${invoice.id}/image`, {
          responseType: "blob",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const blob = res.data;
        if (blob && blob.size > 0) {
          const url = URL.createObjectURL(blob);
          revoked = url;
          setImageUrl(url);
        }
      } catch (err) {
        setImageError(err.response?.status === 404 ? "Зображення відсутнє" : "Не вдалося завантажити");
      } finally {
        setImageLoading(false);
      }
    };
    load();
    return () => {
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [show, invoice?.id]);

  useEffect(() => {
    if (!show || !invoice) return;
    setIsEditing(false);
    setEditVat(invoice.vat_amount != null ? String(Number(invoice.vat_amount).toFixed(2)) : "");
    setEditGross(invoice.gross_amount != null ? String(Number(invoice.gross_amount).toFixed(2)) : "");
    setEditRefundPercent(invoice.refund_percent != null ? String(Number(invoice.refund_percent)) : "50");
    setEditInvoiceFee(invoice.invoice_fee != null ? String(Number(invoice.invoice_fee).toFixed(2)) : "");
    setEditStatus(invoice.status || "uploaded");
    setEditRejectionReason(invoice.rejection_reason || "");
    setEditError("");
    setShowKsefRaw(false);
  }, [show, invoice]);

  const handleSave = () => {
    if (!onSave || !invoice?.id) return;
    if (editStatus === "rejected" && !String(editRejectionReason || "").trim()) {
      setEditError("Вкажіть причину відхилення");
      return;
    }
    const vat = parseFloat(String(editVat).replace(",", "."));
    const gross = parseFloat(String(editGross).replace(",", "."));
    const percent = parseFloat(String(editRefundPercent).replace(",", "."));
    const fee = parseFloat(String(editInvoiceFee).replace(",", "."));
    const vatValue = (!Number.isNaN(vat) && vat >= 0) ? vat : (invoice.vat_amount ?? 0);
    const percentValue = (!Number.isNaN(percent) && percent >= 0) ? percent : (invoice.refund_percent ?? 50);
    const feeValue = (!Number.isNaN(fee) && fee >= 0) ? fee : (invoice.invoice_fee ?? 0);
    const refund = Math.max(vatValue * (percentValue / 100) - feeValue, 0);
    setEditError("");
    setSaving(true);
    onSave({
      vat_amount: vatValue,
      gross_amount: (!Number.isNaN(gross) && gross >= 0) ? gross : (invoice.gross_amount ?? null),
      refund_percent: percentValue,
      invoice_fee: feeValue,
      refund_amount: Math.round(refund * 100) / 100,
      status: editStatus,
      rejection_reason: editStatus === "rejected" ? String(editRejectionReason).trim() : null,
    }, () => setSaving(false));
  };

  const handleDelete = () => {
    if (!invoice?.id) return;
    if (!window.confirm("Видалити фактуру та весь запис? Цю дію не можна скасувати.")) return;
    if (onDelete) {
      onDelete(invoice.id);
    }
  };

  if (!invoice) {
    return (
      <Modal show={show} onHide={onHide} size="lg" centered>
        <Modal.Body>Немає даних про фактуру.</Modal.Body>
      </Modal>
    );
  }

  const driverName = [invoice.driver_first_name, invoice.driver_last_name].filter(Boolean).join(" ") || `ID: ${invoice.driver_id}`;
  const uploadedByName = [invoice.uploaded_by_first_name, invoice.uploaded_by_last_name].filter(Boolean).join(" ");
  const fmt = (v, decimals = 2) => (v != null && v !== "" ? Number(v).toFixed(decimals) : "—");
  const fmtDate = (v) => (v ? new Date(v).toLocaleString("uk-UA") : "—");

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center">
          Фактура #{invoice.id}
          {onSave && !isEditing && (
            <Button
              variant="link"
              size="sm"
              className="ms-2 p-0 align-middle"
              onClick={() => setIsEditing(true)}
              aria-label="Редагувати"
            >
              <AiOutlineEdit size={20} />
            </Button>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col md={6}>
            {isEditing ? (
              <Form className="mb-3">
                <Row className="g-2">
                  <Col xs={6}>
                    <Form.Group>
                      <Form.Label className="small text-muted mb-0">Статус</Form.Label>
                      <Form.Select
                        size="sm"
                        value={editStatus}
                        onChange={(e) => {
                          setEditStatus(e.target.value);
                          setEditError("");
                        }}
                      >
                        <option value="uploaded">Завантажена</option>
                        <option value="approved">Затверджена</option>
                        <option value="paid">Виплачена</option>
                        <option value="rejected">Відхилена</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col xs={6}>
                    <Form.Group>
                      <Form.Label className="small text-muted mb-0">ВАТ (PLN)</Form.Label>
                      <Form.Control
                        size="sm"
                        type="number"
                        step="0.01"
                        min="0"
                        value={editVat}
                        onChange={(e) => setEditVat(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={6}>
                    <Form.Group>
                      <Form.Label className="small text-muted mb-0">Брутто (PLN, необов'язково)</Form.Label>
                      <Form.Control
                        size="sm"
                        type="number"
                        step="0.01"
                        min="0"
                        value={editGross}
                        onChange={(e) => setEditGross(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={6}>
                    <Form.Group>
                      <Form.Label className="small text-muted mb-0">% від VAT</Form.Label>
                      <Form.Control
                        size="sm"
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        value={editRefundPercent}
                        onChange={(e) => setEditRefundPercent(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={6}>
                    <Form.Group>
                      <Form.Label className="small text-muted mb-0">Ціна за фактуру (PLN)</Form.Label>
                      <Form.Control
                        size="sm"
                        type="number"
                        step="0.01"
                        min="0"
                        value={editInvoiceFee}
                        onChange={(e) => setEditInvoiceFee(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  {editStatus === "rejected" && (
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label className="small text-muted mb-0">Причина відхилення</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          size="sm"
                          value={editRejectionReason}
                          onChange={(e) => {
                            setEditRejectionReason(e.target.value);
                            setEditError("");
                          }}
                          placeholder="Наприклад: нечітке фото, невірні дані..."
                          isInvalid={!!editError}
                        />
                        {editError && (
                          <div className="text-danger small mt-1">{editError}</div>
                        )}
                      </Form.Group>
                    </Col>
                  )}
                </Row>
              </Form>
            ) : null}
            <table className="table table-sm table-borderless mb-0" style={{ tableLayout: "fixed", width: "100%" }}>
              <tbody>
                {!isEditing && (
                  <>
                    <tr>
                      <td className="text-muted pe-2" style={{ width: "40%" }}>Статус</td>
                      <td><span className={`badge bg-${statusVariant(invoice.status)}`}>{statusLabel(invoice.status)}</span></td>
                    </tr>
                    {invoice.status === "rejected" && invoice.rejection_reason && (
                      <tr>
                        <td className="text-muted pe-2 align-top">Причина відхилення</td>
                        <td><small className="text-break text-danger">{invoice.rejection_reason}</small></td>
                      </tr>
                    )}
                    <tr>
                      <td className="text-muted pe-2">Водій</td>
                      <td>{driverName}</td>
                    </tr>
                    <tr>
                      <td className="text-muted pe-2">ВАТ</td>
                      <td><strong>{fmt(invoice.vat_amount)} PLN</strong></td>
                    </tr>
                    {invoice.gross_amount != null && (
                      <tr>
                        <td className="text-muted pe-2">Брутто</td>
                        <td>{fmt(invoice.gross_amount)} PLN</td>
                      </tr>
                    )}
                  </>
                )}
                {isEditing && (
                  <tr>
                    <td className="text-muted pe-2" style={{ width: "40%" }}>Водій</td>
                    <td>{driverName}</td>
                  </tr>
                )}
                <tr>
                  <td className="text-muted pe-2">Сума повернення</td>
                  <td><strong className="text-success">{fmt(invoice.refund_amount)} PLN</strong></td>
                </tr>
                {!isEditing && invoice.refund_percent != null && (
                  <tr>
                    <td className="text-muted pe-2">% від VAT</td>
                    <td>{fmt(invoice.refund_percent, 0)}%</td>
                  </tr>
                )}
                {!isEditing && invoice.invoice_fee != null && (
                  <tr>
                    <td className="text-muted pe-2">Ціна за фактуру</td>
                    <td>{fmt(invoice.invoice_fee)} PLN</td>
                  </tr>
                )}
                <tr>
                  <td className="text-muted pe-2">Джерело</td>
                  <td>{sourceLabel(invoice.source)}</td>
                </tr>
                {uploadedByName && (
                  <tr>
                    <td className="text-muted pe-2">Завантажив</td>
                    <td>{uploadedByName}</td>
                  </tr>
                )}
                <tr>
                  <td className="text-muted pe-2">Завантажено</td>
                  <td>{fmtDate(invoice.uploaded_at)}</td>
                </tr>
                <tr>
                  <td className="text-muted pe-2">Затверджено</td>
                  <td>{fmtDate(invoice.approved_at)}</td>
                </tr>
                {invoice.payout_report_id != null && (
                  <tr>
                    <td className="text-muted pe-2">Рапорт виплати</td>
                    <td>#{invoice.payout_report_id}</td>
                  </tr>
                )}
                {invoice.note && (
                  <tr>
                    <td className="text-muted pe-2 align-top">Опис</td>
                    <td className="text-break" style={{ overflowWrap: "anywhere" }}>
                      <small>{invoice.note}</small>
                    </td>
                  </tr>
                )}
                {invoice.ksef_number && (
                  <tr>
                    <td className="text-muted pe-2 align-top">KSeF №</td>
                    <td className="text-break" style={{ overflowWrap: "anywhere" }}>
                      <code className="small">{invoice.ksef_number}</code>
                    </td>
                  </tr>
                )}
                {invoice.ksef_data != null && invoice.ksef_data !== "" && (
                  <tr>
                    <td className="text-muted pe-2 align-top">KSeF (дані)</td>
                    <td>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="mb-1"
                        onClick={() => setShowKsefRaw((prev) => !prev)}
                      >
                        {showKsefRaw ? "Сховати сирі дані" : "Показати сирі дані"}
                      </Button>
                      {showKsefRaw && (
                        <pre
                          className="small bg-light p-2 rounded mb-0"
                          style={{
                            maxHeight: 180,
                            overflow: "auto",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          {prettyPrintKsefData(invoice.ksef_data)}
                        </pre>
                      )}
                    </td>
                  </tr>
                )}
                <tr>
                  <td className="text-muted pe-2">Створено</td>
                  <td><small>{fmtDate(invoice.created_at)}</small></td>
                </tr>
                <tr>
                  <td className="text-muted pe-2">Оновлено</td>
                  <td><small>{fmtDate(invoice.updated_at)}</small></td>
                </tr>
              </tbody>
            </table>
          </Col>
          <Col md={6} className="text-center">
            <InvoiceImageZoom
              imageUrl={imageUrl}
              imageLoading={imageLoading}
              imageError={imageError}
              label="Фото / скан фактури"
            />
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        {isEditing ? (
          <>
            <Button variant="secondary" onClick={() => setIsEditing(false)}>Скасувати</Button>
            <Button variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" className="me-2" /> : null}
              Зберегти
            </Button>
          </>
        ) : (
          <>
            {onDelete && (
              <Button variant="outline-danger" className="me-auto" onClick={handleDelete}>
                <AiOutlineDelete className="me-1" />
                Видалити
              </Button>
            )}
            <Button variant="secondary" onClick={onHide}>Закрити</Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

/**
 * Модальне вікно для перевірки / затвердження фактури.
 * Поки що тільки каркас з кнопками дій.
 *
 * Пропси:
 * - show: boolean
 * - onHide: () => void
 * - invoice: обʼєкт фактури (пізніше використаємо)
 * - onApprove?: () => void — дію підтвердження (пізніше заповнимо)
 * - onReject?: (reason: string) => void — відхилення з причиною
 */
export const DriverInvoiceCheckModal = ({
  show,
  onHide,
  invoice,
  onApprove,
  onReject,
  onDelete,
}) => {
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [grossAmount, setGrossAmount] = useState("");
  const [vatAmount, setVatAmount] = useState("");
  const [invoicePrice, setInvoicePrice] = useState("");
  const [refundPercent, setRefundPercent] = useState("50");
  const [refundAmount, setRefundAmount] = useState("");
  const [hasManualRefund, setHasManualRefund] = useState(false);
  const [description, setDescription] = useState("");
  const [errors, setErrors] = useState({
    vatAmount: "",
    invoicePrice: "",
    refundPercent: "",
  });
  const [imageUrl, setImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState("");
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejectError, setRejectError] = useState("");

  // Ініціалізація при відкритті модалки
  useEffect(() => {
    if (!show || !invoice) {
      return;
    }

    setSelectedDriver(
      invoice.driver_id
        ? {
            value: invoice.driver_id,
            label: `${invoice.driver_first_name || ""} ${invoice.driver_last_name || ""}`.trim(),
          }
        : null
    );
    const initialGross =
      invoice.gross_amount != null ? String(Number(invoice.gross_amount).toFixed(2)) : "";
    setGrossAmount(initialGross);

    if (invoice.vat_amount != null) {
      setVatAmount(String(Number(invoice.vat_amount).toFixed(2)));
    } else {
      setVatAmount("");
    }

    if (invoice.refund_amount != null) {
      setRefundAmount(String(Number(invoice.refund_amount).toFixed(2)));
    } else {
      setRefundAmount("");
    }

    setInvoicePrice(
      invoice.invoice_fee != null ? String(Number(invoice.invoice_fee).toFixed(2)) : ""
    );
    setRefundPercent(
      invoice.refund_percent != null ? String(Number(invoice.refund_percent)) : "50"
    );
    setHasManualRefund(false);
    setDescription(invoice.note || "");
    setRejectMode(false);
    setRejectionReason(invoice.rejection_reason || "");
    setRejectError("");
    setErrors({
      vatAmount: "",
      invoicePrice: "",
      refundPercent: "",
    });

    // Завантажити стандартні значення з налаштувань сайту і підставити в % та ціну за обробку, якщо у фактури ще немає
    const loadSettingsDefaults = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get("/api/settings", {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = res.data || {};
        if (invoice.invoice_fee == null) {
          const v = data.default_invoiceProcessingPrice;
          setInvoicePrice(v != null && v !== "" ? String(Number(v).toFixed(2)) : "");
        }
        if (invoice.refund_percent == null) {
          const v = data.default_invoiceReturnPercent;
          setRefundPercent(v != null && v !== "" ? String(Number(v)) : "50");
        }
      } catch {
        // Ігноруємо помилку — залишаємо поточні значення за замовчуванням
      }
    };
    loadSettingsDefaults();

    // Завантаження зображення фактури
    let revokedUrl = null;
    const loadImage = async () => {
      if (!invoice.id) return;
      setImageLoading(true);
      setImageError("");
      setImageUrl(null);
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`/api/driver-invoices/${invoice.id}/image`, {
          responseType: "blob",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const blobUrl = URL.createObjectURL(res.data);
        setImageUrl(blobUrl);
        revokedUrl = blobUrl;
      } catch (err) {
        // Якщо немає зображення або помилка — просто показуємо плейсхолдер
        setImageError("Зображення недоступне або відсутнє.");
      } finally {
        setImageLoading(false);
      }
    };

    loadImage();

    return () => {
      if (revokedUrl) {
        URL.revokeObjectURL(revokedUrl);
      }
    };
  }, [show, invoice]);

  // Перерахунок суми повернення на основі ВАТ, вказаного водієм (або скоригованого адміном)
  useEffect(() => {
    const vat = parseFloat(String(vatAmount).replace(",", "."));
    if (Number.isNaN(vat) || vat < 0) {
      setRefundAmount("");
      return;
    }

    const invPrice = parseFloat(String(invoicePrice).replace(",", ".")) || 0;
    const percent = parseFloat(refundPercent) || 0;

    // Сума повернення = % від ВАТ, потім повністю віднімаємо ціну за обробку фактури
    const baseRefund = vat * (percent / 100);
    const refund = Math.max(baseRefund - invPrice, 0);

    if (!hasManualRefund) {
      setRefundAmount(refund.toFixed(2));
    }
  }, [vatAmount, invoicePrice, refundPercent, hasManualRefund]);

  const handleApprove = () => {
    if (onApprove) {
      const driverId = selectedDriver?.value ?? invoice?.driver_id;
      const vat = parseFloat(String(vatAmount).replace(",", "."));
      const gross = parseFloat(String(grossAmount).replace(",", "."));
      const refund = parseFloat(String(refundAmount).replace(",", "."));
      const fee = parseFloat(String(invoicePrice).replace(",", "."));
      const percent = parseFloat(String(refundPercent).replace(",", "."));
      if (Number.isNaN(vat) || vat < 0) return;
      onApprove({
        driver_id: driverId,
        vat_amount: vat,
        gross_amount: Number.isNaN(gross) ? null : gross,
        refund_amount: Number.isNaN(refund) ? null : refund,
        invoice_fee: Number.isNaN(fee) ? null : fee,
        refund_percent: Number.isNaN(percent) ? null : percent,
        note: description || null,
        status: "approved",
        approved_at: new Date().toISOString().slice(0, 19).replace("T", " "),
      });
    }
  };

  const handleReject = () => {
    if (!rejectMode) {
      setRejectMode(true);
      setRejectError("");
      return;
    }
    const reason = String(rejectionReason || "").trim();
    if (!reason) {
      setRejectError("Вкажіть причину відхилення");
      return;
    }
    if (onReject) {
      onReject(reason);
    }
  };

  const handleDelete = () => {
    if (!invoice?.id) return;
    if (!window.confirm("Видалити фактуру та весь запис? Цю дію не можна скасувати.")) return;
    if (onDelete) {
      onDelete(invoice.id);
    }
  };

  const validateNumber = (value) => {
    const n = parseFloat(String(value).replace(",", "."));
    return Number.isNaN(n) ? null : n;
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Перевірка фактури</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-2">
              <Form.Label className="mb-1">Водій</Form.Label>
              <DriverSelector
                selectedDriver={selectedDriver}
                setSelectedDriver={setSelectedDriver}
              />
            </Form.Group>

            <Row className="mt-2">
              <Col xs={6}>
                <Form.Group className="mb-2">
                  <Form.Label className="mb-1 small">ВАТ (вказано водієм)</Form.Label>
                  <InputGroup size="sm">
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      value={vatAmount}
                      onChange={(e) => {
                        const val = e.target.value;
                        setVatAmount(val);
                        const n = validateNumber(val);
                        setErrors((prev) => ({
                          ...prev,
                          vatAmount:
                            n === null || n < 0 ? "Введіть суму ВАТ не менше 0" : "",
                        }));
                      }}
                      className="border-success"
                    />
                    <InputGroup.Text>PLN</InputGroup.Text>
                  </InputGroup>
                  {errors.vatAmount && (
                    <div className="text-danger small mt-1">{errors.vatAmount}</div>
                  )}
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group className="mb-2">
                  <Form.Label className="mb-1 small">Брутто (необов'язково)</Form.Label>
                  <InputGroup size="sm">
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      value={grossAmount}
                      onChange={(e) => setGrossAmount(e.target.value)}
                    />
                    <InputGroup.Text>PLN</InputGroup.Text>
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col xs={6}>
                <Form.Group className="mb-2">
                  <Form.Label className="mb-1 small">Ціна за фактуру</Form.Label>
                  <InputGroup size="sm">
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      value={invoicePrice}
                      onChange={(e) => {
                        const val = e.target.value;
                        setInvoicePrice(val);
                        const n = validateNumber(val);
                        setErrors((prev) => ({
                          ...prev,
                          invoicePrice:
                            n === null || n < 0 ? "Ціна має бути не менше 0" : "",
                        }));
                      }}
                    />
                    <InputGroup.Text>PLN</InputGroup.Text>
                  </InputGroup>
                  {errors.invoicePrice && (
                    <div className="text-danger small mt-1">{errors.invoicePrice}</div>
                  )}
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group className="mb-2">
                  <Form.Label className="mb-1 small">% від VAT</Form.Label>
                  <Form.Control
                    size="sm"
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    value={refundPercent}
                    onChange={(e) => {
                      const val = e.target.value;
                      setRefundPercent(val);
                      const n = validateNumber(val);
                      const valid = n !== null && n >= 1 && n <= 100;
                      setErrors((prev) => ({
                        ...prev,
                        refundPercent: valid
                          ? ""
                          : "Відсоток має бути від 1 до 100",
                      }));
                    }}
                  />
                  {errors.refundPercent && (
                    <div className="text-danger small mt-1">{errors.refundPercent}</div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mt-2">
              <Form.Label className="mb-1 small">Опис фактури</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Form.Group>

            {rejectMode && (
              <Form.Group className="mt-3">
                <Form.Label className="mb-1 small text-danger">Причина відхилення</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={rejectionReason}
                  onChange={(e) => {
                    setRejectionReason(e.target.value);
                    setRejectError("");
                  }}
                  placeholder="Наприклад: нечітке фото, невірні дані, фактура не відповідає вимогам..."
                  isInvalid={!!rejectError}
                  autoFocus
                />
                {rejectError && (
                  <div className="text-danger small mt-1">{rejectError}</div>
                )}
              </Form.Group>
            )}

            <Form.Group className="mt-2">
              <Form.Label className="mb-1 small">Сума повернення</Form.Label>
              <InputGroup size="sm">
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  value={refundAmount}
                  onChange={(e) => {
                    setRefundAmount(e.target.value);
                    setHasManualRefund(true);
                  }}
                  className="border-success"
                />
                <InputGroup.Text>PLN</InputGroup.Text>
              </InputGroup>
            </Form.Group>
          </Col>
          <Col md={6} className="text-center">
            <InvoiceImageZoom
              imageUrl={imageUrl}
              imageLoading={imageLoading}
              imageError={imageError}
              label="Фото / скан фактури"
            />
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        {rejectMode ? (
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setRejectMode(false);
                setRejectError("");
              }}
            >
              Назад
            </Button>
            <Button variant="danger" onClick={handleReject}>
              <AiOutlineClose className="me-1" />
              Підтвердити відхилення
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={onHide}>
              Закрити
            </Button>
            {onDelete && (
              <Button variant="outline-danger" onClick={handleDelete}>
                <AiOutlineDelete className="me-1" />
                Видалити
              </Button>
            )}
            <Button variant="danger" onClick={handleReject}>
              <AiOutlineClose className="me-1" />
              Відхилити
            </Button>
            <Button
              variant="success"
              onClick={handleApprove}
              disabled={!!errors.vatAmount || vatAmount === "" || Number.isNaN(parseFloat(String(vatAmount).replace(",", ".")))}
            >
              <AiOutlineCheck className="me-1" />
              Затвердити
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

